import { useEffect } from 'react'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import type { EditorFormModel, EditorSettingName } from '@/msgdeskRuntime/contracts/editorModel'
import { useEditorFormModelSnapshot } from '../../EditorTemplateContract'

export interface SettingSelectGroup {
  master: EditorSettingName
  combos: readonly EditorSettingName[]
}

export const settingCombosByMaster: Partial<Record<EditorSettingName, readonly EditorSettingName[]>> = {
  is_view: ['is_view_combo'],
  is_view_answers: ['is_view_answers_combo'],
  is_view_msges: ['is_view_msges_combo'],
  is_forbid: ['is_forbid_combo'],
  is_fold: ['is_fold_combo'],
  is_freeze: ['is_freeze_combo'],
  is_hide: ['is_hide_combo'],
  is_view_count: ['is_view_count_0', 'is_view_count_1', 'is_view_count_2', 'is_view_count_3', 'is_view_count_4', 'is_view_count_5'],
}

const settingMasterByCombo = new Map<EditorSettingName, EditorSettingName>()
for (const [master, combos] of Object.entries(settingCombosByMaster) as Array<[EditorSettingName, readonly EditorSettingName[]]>) {
  for (const combo of combos) settingMasterByCombo.set(combo, master)
}

function enabled(value: unknown): boolean {
  return value === true || value === 1 || value === '1'
}

function normalizedCombo(value: unknown, groupEnabled: boolean): number {
  const numeric = Number(value)
  if (!groupEnabled) return 0
  return Number.isFinite(numeric) && numeric >= 1 ? numeric : 1
}

/**
 * One state contract for every checkbox-controlled Configure dropdown.
 * The checkbox owns availability; enabled dropdowns always carry a usable
 * value, while disabled pairs persist legacy zero semantics.
 */
export function useSettingSelectGroups(groups: readonly SettingSelectGroup[]) {
  const model = useEditorFormModelSnapshot()

  useEffect(() => {
    if (!model) return

    // Apply one correction per model identity. Each semantic write publishes
    // the next model, so subsequent inconsistent pairs are handled without
    // writing a batch from a stale snapshot.
    for (const group of groups) {
      const groupEnabled = enabled(model.settings[group.master])
      for (const combo of group.combos) {
        const current = Number(model.settings[combo])
        const next = normalizedCombo(current, groupEnabled)
        if (Number.isFinite(current) && current === next) continue
        dispatchEditorAction({
          action: 'set-more-setting',
          name: combo,
          value: next,
          reason: `configure-pair:${group.master}:${groupEnabled ? 'enable' : 'disable'}`,
        })
        return
      }
    }
  }, [groups, model])

  return {
    model,
    isEnabled(master: EditorSettingName): boolean {
      return enabled(model?.settings[master])
    },
    selectValue(combo: EditorSettingName): string {
      const numeric = Number(model?.settings[combo])
      return String(Number.isFinite(numeric) && numeric >= 1 ? numeric : 1)
    },
  }
}

// Controlled inputs dispatch through SettingsInputs' delegated change
// handler. Supplying the local handler tells React the control is intentionally
// writable while the semantic model remains the single value owner.
export function delegatedSettingChange() {}

function namedInput(root: HTMLElement, name: EditorSettingName): HTMLInputElement | undefined {
  return Array.from(root.getElementsByTagName('input')).find(element => element.name === name)
}

function namedSelect(root: HTMLElement, name: EditorSettingName): HTMLSelectElement | undefined {
  return Array.from(root.getElementsByTagName('select')).find(element => element.name === name)
}

/** Remove legacy's stale disabled attribute in the same input event. */
export function syncSettingSelectDom(root: HTMLElement, target: EventTarget) {
  let master: HTMLInputElement | undefined
  let combos: readonly EditorSettingName[] | undefined

  if (target instanceof HTMLInputElement && target.type === 'checkbox') {
    master = target
    combos = settingCombosByMaster[target.name as EditorSettingName]
  } else if (target instanceof HTMLSelectElement) {
    const masterName = settingMasterByCombo.get(target.name as EditorSettingName)
    if (masterName) {
      master = namedInput(root, masterName)
      combos = settingCombosByMaster[masterName]
    }
  }
  if (!master || !combos) return

  const apply = () => {
    for (const comboName of combos) {
      const combo = namedSelect(root, comboName)
      if (!combo) continue
      combo.disabled = !master.checked
      if (master.checked && Number(combo.value) < 1) combo.value = '1'
    }
  }
  apply()
  // Legacy's native handler can run after React's delegated change handler.
  // Reassert once on the next frame so a stale attr("disabled", true) cannot
  // win the event race.
  requestAnimationFrame(apply)
}

/** Reassert every pair's combo disabled state from its master CHECKBOX DOM
 * state. Runs from the settings MutationObserver against legacy's stale
 * cross-pair attr("disabled", …) writes. Deliberately reads the DOM, not the
 * model: at mutation-checkpoint timing the model may not have absorbed the
 * user's click yet, and writing checkboxes from a stale model would revert
 * the click before React's handler reads it. Difference-guarded writes only,
 * so the observer converges instead of storming. */
export function reassertSettingSelectDisabled(root: HTMLElement) {
  for (const combos of Object.values(settingCombosByMaster) as Array<readonly EditorSettingName[]>) {
    let master: HTMLInputElement | undefined
    for (const comboName of combos) {
      const combo = namedSelect(root, comboName)
      if (!combo) continue
      master ??= namedInput(root, settingMasterByCombo.get(comboName) as EditorSettingName)
      if (!master) break
      if (combo.disabled !== !master.checked) combo.disabled = !master.checked
    }
  }
}

/** Project the authoritative rough model into one mounted Configure copy.
 * Every write is difference-guarded: this projector also runs from the
 * settings MutationObserver, and attribute sets queue mutation records even
 * when the value is IDENTICAL — an unguarded write would re-trigger the
 * observer in an endless microtask storm that freezes the page. */
export function projectSettingSelectDom(root: HTMLElement, model: EditorFormModel | undefined) {
  if (!model) return
  for (const [masterName, combos] of Object.entries(settingCombosByMaster) as Array<[EditorSettingName, readonly EditorSettingName[]]>) {
    const master = namedInput(root, masterName)
    if (!master) continue
    const groupEnabled = enabled(model.settings[masterName])
    if (master.checked !== groupEnabled) master.checked = groupEnabled
    for (const comboName of combos) {
      const combo = namedSelect(root, comboName)
      if (!combo) continue
      if (combo.disabled !== !groupEnabled) combo.disabled = !groupEnabled
      const value = normalizedCombo(model.settings[comboName], groupEnabled)
      if (groupEnabled && combo.value !== String(value)) combo.value = String(value)
    }
  }
}
